import type { SyncStatus } from '@/store/appStore'

interface Props {
  isOnline: boolean
  syncStatus: SyncStatus
  syncError: string | null
  pendingCount: number
  onSyncNow: () => void
  onForceSync: () => void
  onUploadNow: () => void
}

export default function SyncStatusBar({ isOnline, syncStatus, syncError, pendingCount, onSyncNow, onForceSync, onUploadNow }: Props) {
  if (!isOnline) {
    return (
      <div className="bg-yellow-500 text-yellow-900 text-center text-sm py-1.5 px-4 font-medium">
        Offline — changes will sync when you reconnect
      </div>
    )
  }

  if (syncStatus === 'error') {
    return (
      <div className="bg-[#fef2f2] border-b border-[#fecaca] px-4 py-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[#dc2626]">⚠ Sync failed</p>
            {syncError && (
              <p className="text-[10px] text-[#ef4444] mt-0.5 truncate">{syncError}</p>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={onForceSync} className="text-xs font-medium text-[#dc2626] underline py-1">
              Force sync
            </button>
            <button onClick={onSyncNow} className="text-xs font-medium text-[#dc2626] py-1">
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (syncStatus === 'syncing') {
    return (
      <div className="bg-[#f8fafc] dark:bg-[#1e293b] border-b border-[#e2e8f0] dark:border-[#334155] px-4 py-1.5 flex items-center gap-2">
        <div className="w-3 h-3 border-2 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
        <span className="text-xs text-[#64748b] dark:text-[#94a3b8]">Syncing…</span>
      </div>
    )
  }

  if (pendingCount > 0) {
    return (
      <div className="bg-[#fefce8] dark:bg-[#713f12]/40 border-b border-[#fde047]/60 px-4 py-1.5 flex items-center justify-between gap-2">
        <p className="text-xs text-[#854d0e] dark:text-[#fde047]">
          {pendingCount} stat {pendingCount === 1 ? 'entry' : 'entries'} waiting to upload
        </p>
        <button onClick={onUploadNow} className="text-xs font-semibold text-[#854d0e] dark:text-[#fde047] underline py-1 shrink-0">
          Upload now
        </button>
      </div>
    )
  }

  // Hide bar when idle + online — no noise needed
  return null
}
